import * as RadixToast from '@radix-ui/react-toast';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface ToastInput {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** Milliseconds on screen. Errors stay longer. */
  duration?: number;
  /** One button under the message, for a toast that offers to do something. */
  action?: React.ReactNode;
  /** Called once when the toast leaves, however it leaves. */
  onDismiss?: () => void;
}

interface ToastItem extends ToastInput {
  id: number;
}

interface ToastApi {
  show: (input: ToastInput) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const toneIcon = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
};

const toneColor: Record<ToastTone, string> = {
  info: 'text-primary',
  success: 'text-[color:var(--ts-pill-success-text)]',
  warning: 'text-[color:var(--ts-pill-warning-text)]',
  danger: 'text-destructive',
};

/** Simple toasts, top right, four seconds. */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const show = useCallback((input: ToastInput) => {
    setItems((current) => [...current, { ...input, id: Date.now() + Math.random() }]);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (title, description) => show({ title, description, tone: 'success' }),
      error: (title, description) => show({ title, description, tone: 'danger', duration: 8000 }),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      <RadixToast.Provider swipeDirection="right" duration={4000}>
        {children}
        {items.map((item) => {
          const tone = item.tone ?? 'info';
          const Icon = toneIcon[tone];
          return (
            <RadixToast.Root
              key={item.id}
              duration={item.duration}
              onOpenChange={(open) => {
                if (open) return;
                item.onDismiss?.();
                setItems((current) => current.filter((entry) => entry.id !== item.id));
              }}
              className={cn(
                'flex items-start gap-3 rounded-lg border border-[color:var(--ts-border-soft)] bg-surface p-4',
                'shadow-float data-[state=open]:anim-slide-in-right data-[state=closed]:anim-fade-out',
              )}
            >
              <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', toneColor[tone])} aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <RadixToast.Title className="text-base font-medium text-gray-3">{item.title}</RadixToast.Title>
                {item.description ? (
                  <RadixToast.Description className="mt-0.5 text-sm break-words text-muted-foreground">
                    {item.description}
                  </RadixToast.Description>
                ) : null}
                {item.action ? <div className="mt-3 flex flex-wrap gap-2">{item.action}</div> : null}
              </div>
              <RadixToast.Close
                aria-label="Dismiss"
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-gray-1',
                  'transition-colors duration-150 ease-out hover:bg-[color:var(--ts-row-hover)] hover:text-primary',
                )}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </RadixToast.Close>
            </RadixToast.Root>
          );
        })}
        <RadixToast.Viewport className="fixed top-[76px] right-4 z-[60] flex w-[380px] max-w-[calc(100vw-2rem)] flex-col gap-2 outline-none" />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return context;
}
